'use client';

import { useMemo, useCallback } from 'react';
import { useClientTranslation } from '@bts-web/utils-lokalise';
import { Drawer } from '../../../common/components/Drawer/Drawer';
import { useParams, useRouter } from 'next/navigation';
import { TradeOptionGroup, TradeOptionsItems } from './TradeOptionGroup';
import { NEXT_ROUTES } from '@bts-web/utils-next-infrastructure';
import { TradeControllerVariants } from '../../utils';
import { getAssetTradeUrl } from '../../../route-builders';
import { AssetSelectDrawer } from '../../../common/components/Drawers/AssetSelectDrawer/AssetSelectDrawer';
import { useClientUserInfo } from '../../../common/userinfo/InitialUserInfoFetcherAndParser/utils/useClientUserInfo';
import { useTradeContext } from '../../TradeContext/TradeContext.use-client';
import { TransfersLoaderClient } from '../../../crypto-transfers/components/TransfersLoader/TransfersLoader.client';
import { TransfersSectionVariants } from '../../../crypto-transfers';
import {
  RegularBuy,
  RegularClockPlusOutlineAlt,
  RegularDeposits,
  RegularSell,
  RegularStakingReward,
  RegularSwap,
  RegularWithdraw,
} from '../../../common/svgIcons';
import { useModalDrawerContext } from '../../../common/components/ModalDrawerContext/ModalDrawerContext';
import { RoleCheckActionType } from '../../../common/utils/userAccess/utils';
import { useUserAccessControl } from '../../../common/utils/userAccess/userAccessControl';
import { Tag } from '../../../common';
import { StakingAssetSelectDrawer } from '../../../staking/components/modalsDrawers/StakingAssetSelectDrawer/StakingAssetSelectDrawer';
import { StartSavingModal } from '../../../savings-plans/StartSavingModal/StartSavingModal';
import { useFeatureFlag } from '../../../statsig/sdk/provider';

export const CryptoTransfersClientDrawer = (type: TransfersSectionVariants) => {
  return (
    <Drawer hideDefaultHeader>
      <TransfersLoaderClient transferType={type} />
    </Drawer>
  );
};

interface TradeOptionGroupConfig {
  key: string;
  label?: string;
  items: TradeOptionsItems[];
}

const TradeOptionsDrawer = ({
  isSellDisabled = false,
  isBuyDisabled = false,
  isMarketOpen,
  isOwningAsset,
}: {
  isBuyDisabled?: boolean;
  isSellDisabled?: boolean;
  isOwningAsset?: boolean;
  isMarketOpen?: boolean;
}) => {
  const params = useParams();

  const { assetId: paramAssetId } = params || {};

  const feature_hasSavingsPlansEnabled = useFeatureFlag(
    'feature_hasSavingsPlansEnabled',
  );

  const feature_hasStakingEnabled = useFeatureFlag('feature_hasStakingEnabled');

  const feature_hasSwapTradeEnabled = useFeatureFlag(
    'feature_hasSwapTradeEnabled',
  );

  const feature_showSwapTradeNewLabel = useFeatureFlag(
    'feature_showSwapTradeNewLabel',
  );

  const { showModal, openDrawer, closeAll } = useModalDrawerContext();

  const { t } = useClientTranslation();

  const { isSellingDisabled, isPortfolioEmpty, isUserHavingSavingsPlans } =
    useTradeContext();

  const { interceptAction } = useUserAccessControl();

  const { fiat, withdrawalsActive, isIslamicUser } = useClientUserInfo();

  const feature_cryptoTransfers = useFeatureFlag(
    'feature_hasCryptoTransfersEnabled',
  );

  const sellButtonDisabled =
    isPortfolioEmpty ||
    isSellDisabled ||
    isSellingDisabled ||
    isOwningAsset === false ||
    !!(paramAssetId && typeof paramAssetId !== 'string');

  const swapButtonDisabled =
    isPortfolioEmpty || isSellDisabled || isSellingDisabled;

  const withdrawalButtonDisabled =
    !withdrawalsActive || isPortfolioEmpty || isSellingDisabled;

  const stakeButtonDisabled = isPortfolioEmpty || isOwningAsset === false;

  const router = useRouter();

  // Helper to create click handlers that work with the TradeOptionsItems interface
  const createHandler = (
    actionType: RoleCheckActionType,
    callback: () => void,
  ) => {
    return () => {
      // Create a proper mock event with the required methods
      const mockEvent = {
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        preventDefault: () => {},
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        stopPropagation: () => {},
      } as React.MouseEvent<HTMLButtonElement>;

      const handler = interceptAction(actionType, callback);

      handler(mockEvent);
    };
  };

  const getAssetUrl = useCallback(
    (operation: TradeControllerVariants, swapWithAssetId?: string) => {
      if (typeof paramAssetId !== 'string' || !paramAssetId)
        return `/discover?next=${operation}`;

      if (operation === 'buy') {
        return getAssetTradeUrl(paramAssetId, { type: 'buy' });
      }

      if (operation === 'sell') {
        return getAssetTradeUrl(paramAssetId, { type: 'sell' });
      }

      const basePath = NEXT_ROUTES.ASSET_DETAILS.replace('[id]', paramAssetId);

      return `${basePath}/${operation}${swapWithAssetId ? `?swapWith=${swapWithAssetId}` : ''}`;
    },
    [paramAssetId],
  );

  const navigateTo = (path: string) => {
    router.push(path);

    closeAll();
  };

  const translations = useMemo(
    () => ({
      dashboardDrawerTopBar: t('dashboard_drawer_top_bar'),
      dashboardDrawerBuy: t('buy'),
      dashboardDrawerSell: t('sell'),
      dashboardDrawerSwap: t('swap'),
      dashboardDrawerNew: t('dashboard_drawer_new'),
      dashboardDrawerSavingsPlan: t('savings_plan'),
      dashboardDrawerWallet: t('wallet'),
      dashboardDrawerEarn: t('earn'),
      dashboardDrawerDeposit: t('deposit'),
      dashboardDrawerWithdraw: t('tx_details_type_withdrawal'),
      dashboardDrawerPayWith: t('pay_with'),
      dashboardDrawerWhatYouGet: t('what_you_get'),
      dashboardDrawerStaking: t('staking'),
    }),
    [t],
  );

  const openDestinationDrawer = (sourceId: string) => {
    openDrawer(AssetSelectDrawer, {
      onSelect: (asset) => {
        const targetId = asset.id;

        if (targetId === fiat.symbol) {
          router.push(getAssetTradeUrl(sourceId ?? '', { type: 'sell' }));
        } else {
          router.push(
            `${NEXT_ROUTES.ASSET_DETAILS.replace('[id]', sourceId ?? '')}/swap?swapWith=${targetId}`,
          );
        }
      },
      showFiatOption: true,
      title: translations.dashboardDrawerWhatYouGet,
      selectedAssetId: sourceId,
      showDisclaimer: true,
    });
  };

  const handlers = {
    onClickTradeBuy: createHandler(RoleCheckActionType.BUY, () => {
      navigateTo(getAssetUrl('buy'));
    }),

    onClickTradeSell: createHandler(RoleCheckActionType.SELL, () =>
      navigateTo(paramAssetId ? getAssetUrl('sell') : '/my-assets'),
    ),

    onClickSwap: createHandler(RoleCheckActionType.SWAP, () => {
      closeAll();

      openDrawer(AssetSelectDrawer, {
        onSelect: (asset: { id: string }) => {
          const sourceId =
            isOwningAsset && typeof paramAssetId === 'string'
              ? paramAssetId
              : asset.id;

          const targetId = isOwningAsset ? asset.id : undefined;

          if (!targetId) {
            openDestinationDrawer(sourceId as string);
          } else {
            const basePath = NEXT_ROUTES.ASSET_DETAILS.replace(
              '[id]',
              sourceId ?? '',
            );

            router.push(`${basePath}/swap?swapWith=${targetId}`);
          }
        },
        ownedOnly: !isOwningAsset,
        title: isOwningAsset
          ? translations.dashboardDrawerWhatYouGet
          : translations.dashboardDrawerPayWith,
        position: 'right',
        selectedAssetId:
          typeof paramAssetId === 'string' ? paramAssetId : undefined,
      });
    }),

    onClickSavings: createHandler(RoleCheckActionType.BUY, () => {
      if (isUserHavingSavingsPlans) {
        navigateTo(getAssetUrl('savings'));
      } else {
        closeAll();

        showModal(StartSavingModal, {
          continueUrl: '/discover?next=savings',
        });
      }
    }),

    onClickStaking: createHandler(RoleCheckActionType.STAKE, () => {
      closeAll();

      if (paramAssetId) {
        navigateTo(getAssetUrl('stake'));
      } else {
        openDrawer(StakingAssetSelectDrawer);
      }
    }),

    onClickDeposit: createHandler(RoleCheckActionType.DEPOSIT, () => {
      closeAll();

      openDrawer(
        () => CryptoTransfersClientDrawer(TransfersSectionVariants.Deposit),
        {
          disableCloseButton: true,
          disableOverlayClose: true,
        },
      );
    }),

    onClickWithdrawal: createHandler(RoleCheckActionType.WITHDRAW, () => {
      closeAll();

      openDrawer(
        () => CryptoTransfersClientDrawer(TransfersSectionVariants.Send),
        {
          disableCloseButton: true,
          disableOverlayClose: true,
        },
      );
    }),
  };

  const groups: TradeOptionGroupConfig[] = [
    {
      key: 'trade',
      items: [
        {
          testId: 'trade-options-drawer-buy-btn',
          title: translations.dashboardDrawerBuy,
          icon: <RegularBuy />,
          onClick: handlers.onClickTradeBuy,
          disabled: isBuyDisabled || isMarketOpen === false,
        },
      ],
    },
  ];

  groups[0].items.push({
    testId: 'trade-options-drawer-sell-btn',
    title: translations.dashboardDrawerSell,
    icon: <RegularSell />,
    onClick: handlers.onClickTradeSell,
    disabled: sellButtonDisabled,
  });

  if (feature_hasSwapTradeEnabled && !isIslamicUser) {
    groups[0].items.push({
      testId: 'trade-options-drawer-swap-btn',
      title: translations.dashboardDrawerSwap,
      icon: <RegularSwap />,
      ...(feature_showSwapTradeNewLabel
        ? {
            tag: (
              <Tag visual="filledInfoStatus" size="medium">
                {translations.dashboardDrawerNew}
              </Tag>
            ),
          }
        : {}),
      onClick: handlers.onClickSwap,
      disabled: swapButtonDisabled,
    });
  }

  if (feature_cryptoTransfers) {
    groups.push({
      key: 'wallet',
      label: translations.dashboardDrawerWallet,
      items: [
        {
          testId: 'trade-options-drawer-deposit-btn',
          title: translations.dashboardDrawerDeposit,
          icon: <RegularDeposits />,
          onClick: handlers.onClickDeposit,
        },
        {
          testId: 'trade-options-drawer-withdrawal-btn',
          title: translations.dashboardDrawerWithdraw,
          icon: <RegularWithdraw size={20} />,
          onClick: handlers.onClickWithdrawal,
          disabled: withdrawalButtonDisabled,
        },
      ],
    });
  }

  const showSavings = feature_hasSavingsPlansEnabled;

  const showStaking = feature_hasStakingEnabled && !isIslamicUser;

  if (showSavings || showStaking) {
    groups.push({
      key: 'earn',
      label: translations.dashboardDrawerEarn,
      items: [
        ...(showSavings
          ? [
              {
                testId: 'trade-options-drawer-savings-btn',
                title: translations.dashboardDrawerSavingsPlan,
                icon: <RegularClockPlusOutlineAlt />,
                onClick: handlers.onClickSavings,
                disabled: isBuyDisabled,
              },
            ]
          : []),
        ...(showStaking
          ? [
              {
                testId: 'trade-options-drawer-staking-btn',
                title: translations.dashboardDrawerStaking,
                icon: <RegularStakingReward />,
                onClick: handlers.onClickStaking,
                disabled: stakeButtonDisabled,
              },
            ]
          : []),
      ],
    });
  }

  return (
    <Drawer title={translations.dashboardDrawerTopBar}>
      {groups.map((group) => (
        <TradeOptionGroup
          key={group.key}
          items={group.items}
          label={group.label || ''}
        />
      ))}
    </Drawer>
  );
};

export { TradeOptionsDrawer };
